import React, { useContext, useState, useCallback } from 'react';

import styled from 'styled-components';

import { ResourceSelectorDetails } from '@data-exploration/containers';
import { Splitter } from '@data-exploration-components/components';
import { ResourceItem } from '@data-exploration-components/types';

import { SIDEBAR_RESIZE_EVENT } from '@data-exploration-lib/core';

export type ResourcePreviewProps = {
  /**
   * The resource to preview, this is the easiest way to preview something with the default resource preview
   */
  item?: ResourceItem;
  /**
   * Additional header
   */
  header?: React.ReactNode;
  /**
   * Additional footer
   */
  footer?: React.ReactNode;
  /**
   * Replace the content (would cover over the item preview)
   */
  content?: React.ReactNode;
  /**
   * Placeholder for when no item is provided
   */
  placeholder?: React.ReactNode;
  /**
   * Is the preview closable, default to `true`
   */
  closable?: boolean;
  /**
   * Callback for when the preview is closed
   */
  onClose?: () => void;
};

export type ResourcePreviewObserver = {
  /**
   * Opens the preview for a resource
   */
  openPreview: (props?: ResourcePreviewProps) => void;
  /**
   * Closes the current preview
   */
  hidePreview: () => void;
};

const ResourcePreviewContext = React.createContext(
  {} as ResourcePreviewObserver
);

export const useResourcePreview = () => {
  const observer = useContext(ResourcePreviewContext);
  return observer;
};

export const ResourcePreviewProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [header, setHeader] = useState<React.ReactNode>(null);
  const [footer, setFooter] = useState<React.ReactNode>(null);
  const [content, setContent] = useState<React.ReactNode>(null);
  const [closable, setClosable] = useState<boolean>(true);
  const [placeholder, setPlaceholder] = useState<React.ReactNode>(null);
  const [onClose, setOnCloseCallback] = useState<() => void>(() => () => {});
  const [item, setItem] = useState<ResourceItem | undefined>(undefined);

  const openPreview = useCallback(
    (previewDetails: ResourcePreviewProps = {}) => {
      const {
        item: newItem,
        content: newContent,
        header: newHeader,
        footer: newFooter,
        placeholder: newPlaceholder,
        onClose: newOnClose = () => {},
        closable: newClosable = true,
      } = previewDetails;
      setItem(newItem);
      setContent(newContent);
      setHeader(newHeader);
      setFooter(newFooter);
      setPlaceholder(newPlaceholder);
      setClosable(newClosable);
      setOnCloseCallback(() => newOnClose);
      setIsOpen(true);
    },
    []
  );

  const hidePreview = useCallback(() => {
    setIsOpen(false);
    setItem(undefined);
    setContent(null);
    setHeader(null);
    setFooter(null);
    setPlaceholder(null);
    setOnCloseCallback(() => () => {});
  }, []);

  // TODO Needs to remove in storybook
  return (
    <ResourcePreviewContext.Provider
      value={{
        openPreview,
        hidePreview,
      }}
    >
      <Splitter>
        <ChildrenWrapper>{children}</ChildrenWrapper>
        {isOpen && (
          <ResourceSelectorDetails
            item={item}
            header={header}
            footer={footer}
            content={content}
            placeholder={placeholder}
            closable={closable}
            onClose={() => {
              setIsOpen(false);
              onClose();
              setTimeout(
                () => window.dispatchEvent(new Event(SIDEBAR_RESIZE_EVENT)),
                200
              );
            }}
          />
        )}
      </Splitter>
    </ResourcePreviewContext.Provider>
  );
};

const ChildrenWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export default ResourcePreviewContext;
